import Link from 'next/link'

import { Poster } from '@/app/components/atoms/Poster'
import { Price } from '@/app/components/atoms/Price'
import { PriceTag } from '@/app/components/atoms/PriceTag'
import { Sticker } from '@/app/components/atoms/Sticker'
import { Tag } from '@/app/components/atoms/Tag'
import { BazaarvoiceInlineRating } from '@/app/components/shared/BazaarvoiceInlineRating'
import { POSTER_FALLBACK_COLOR } from '@/app/components/shared/cards/VerticalCard/constants'
import { cx } from '@/app/lib/utils'
import { getRatingEnvironment } from '@/app/lib/ratings'

import { PurchaseCta } from '../PurchaseCta'
import type { HorizontalCardData } from './types'
import styles from './HorizontalCard.module.scss'

interface HorizontalCardProps extends HorizontalCardData {
  readonly className?: string
}

/**
 * `HorizontalCard` — tarjeta de listado genérica: imagen a la izquierda
 * (ratio cuadrado) y contenido a la derecha, en todos los breakpoints.
 * Paridad visual con el patrón "card horizontal" de referencia
 * (`eci-firefly-uikit`, reimplementado con clases/tokens propios).
 * Misma forma de datos que `VerticalCard` (`VerticalCardData`) — solo cambia
 * la disposición.
 *
 * Componente de presentación puro → Server Component. Comparte con
 * `VerticalCard` la lógica de estado de compra vía `PurchaseCta` (badge +
 * CTA con doble destino/deshabilitado), incluido el patrón "stretched link"
 * sobre el título (specs/019-estados-compra-eventos).
 */
export function HorizontalCard({
  productId,
  title,
  href,
  img,
  date,
  venue,
  priceDisplay,
  tag,
  sticker,
  ctaLabel,
  imageBadge,
  ctaHref,
  ctaDisabled,
  showCta = true,
  className,
}: HorizontalCardProps) {
  const { minRatingsThreshold, bazaarvoiceScriptUrl } = getRatingEnvironment()

  return (
    <article className={cx(styles.card, className)}>
      <div
        className={cx(styles.imageWrap, imageBadge && styles.imageWrapMuted)}
        style={!img ? { background: POSTER_FALLBACK_COLOR } : undefined}
      >
        {img && <Poster src={img} alt="" className={styles.poster} />}

        {/* Categoría — esquina superior izquierda de la imagen. Se oculta
            cuando hay estado de compra (paridad con la referencia). */}
        {tag && !imageBadge && (
          <Tag variant="black" className={styles.tagTopLeft}>
            {tag}
          </Tag>
        )}

        {/* Badge de descuento/promoción — esquina superior derecha de la
            imagen (paridad con la referencia). */}
        <PriceTag
          tag={priceDisplay?.tag ?? null}
          hasAppPromotions={priceDisplay?.hasAppPromotions}
          className={styles.tagTopRight}
        />

        {imageBadge && (
          <div className={styles.imageBadge}>
            <Tag variant="status">{imageBadge}</Tag>
          </div>
        )}

        {/* Sticker comercial — esquina inferior izquierda de la imagen. Se
            oculta con estado de compra (igual que la categoría): un evento
            no comprable no promociona "Últimas entradas". */}
        {sticker && !imageBadge && (
          <div className={styles.stickerBottomLeft}>
            <Sticker>{sticker}</Sticker>
          </div>
        )}
      </div>

      {/* Orden del cuerpo: fecha → título → ciudad → precio → CTA, igual que
          `VerticalCard`. La posición real la fija `grid-template-areas` en el
          SCSS; el orden del DOM se mantiene alineado con el visual a propósito,
          para que la lectura por lector de pantalla siga la misma secuencia
          (WCAG 2.1 AA — 1.3.2). */}
      <div className={styles.body}>
        {date && <p className={styles.date}>{date}</p>}

        {/* `href` vacío ⇒ producto sin destino derivable: título como texto
            plano, nunca un enlace muerto al documento actual (mismo criterio que
            `VerticalCard` — specs/023-productos-destacados-module). */}
        <h2 className={styles.title}>
          {href ? (
            <Link href={href} className={styles.titleLink}>
              {title}
            </Link>
          ) : (
            title
          )}
        </h2>

        {venue && <p className={styles.venue}>{venue}</p>}

        {/* Estrella + puntuación de Bazaarvoice, igual que `VerticalCard`
            (spec.md FR-003/FR-004). */}
        <BazaarvoiceInlineRating
          productId={productId}
          threshold={minRatingsThreshold}
          scriptUrl={bazaarvoiceScriptUrl}
        />

        {/* Precio apilado justo encima del CTA (no a su izquierda como en la
            referencia): el bloque queda anclado al borde inferior por la fila
            "spacer" del grid. */}
        {priceDisplay && (
          <div className={styles.price}>
            <Price
              amount={priceDisplay.amount}
              isFree={priceDisplay.isFree}
              originalAmount={priceDisplay.originalAmount ?? undefined}
              variant={priceDisplay.originalAmount != null ? 'offer' : 'default'}
              showFrom
            />
          </div>
        )}

        {/* `showCta={false}` omite el CTA por completo — la card queda "solo
            enlace" (ver `VerticalCard/types.ts`, tipo compartido). */}
        {showCta && (
          <PurchaseCta
            href={href}
            ctaLabel={ctaLabel}
            ctaHref={ctaHref}
            ctaDisabled={ctaDisabled}
            fullWidth={false}
            className={styles.cta}
          />
        )}
      </div>
    </article>
  )
}
